import { Baby, CheckCircle2, CarTaxiFront, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const CHILD_SEAT_STEPS = [
  {
    icon: Baby,
    title: 'Tell Us Age & Weight',
    desc: 'Include your child\'s age and weight when you request your ride.',
  },
  {
    icon: CheckCircle2,
    title: 'We Match the Right Seat',
    desc: 'We select the correct car seat based on your child\'s size.',
  },
  {
    icon: CarTaxiFront,
    title: 'Seat Pre-Installed on Arrival',
    desc: 'Your driver arrives with the child seat already installed and ready.',
  },
];

export default function ChildSeatBand() {
  return (
    <section
      className="section-pad"
      style={{ background: 'linear-gradient(to bottom, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
      data-testid="child-seat-trust-band"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
        {/* Centered header */}
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary">
            Pre-Installed Child Seats
          </h2>
          <p className="mt-3 text-muted-foreground text-base sm:text-lg">
            Pre-installed upon request — just let us know when you book.
          </p>
        </div>

        {/* Steps + connecting line */}
        <div className="relative mt-12 sm:mt-16">
          {/* Horizontal connecting line — desktop/tablet only */}
          <div
            aria-hidden="true"
            className="hidden sm:block absolute left-[16.66%] right-[16.66%] top-[84px] h-px bg-gradient-to-r from-primary/0 via-primary/35 to-primary/0"
          />

          <ol className="relative grid grid-cols-1 sm:grid-cols-3 gap-y-14 sm:gap-y-0 sm:gap-x-6">
            {CHILD_SEAT_STEPS.map((step, i) => (
              <li key={step.title} className="flex flex-col items-center text-center">
                {/* Square icon pill */}
                <div
                  className="relative w-20 h-20 rounded-2xl bg-accent/55 border border-primary/15 flex items-center justify-center shadow-[var(--shadow-soft)]"
                  data-testid={`child-step-${i + 1}-pill`}
                >
                  <step.icon
                    className="h-8 w-8 text-primary"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                </div>

                {/* Anchor dot on the connecting line (desktop/tablet only) */}
                <span
                  aria-hidden="true"
                  className="hidden sm:block w-2.5 h-2.5 rounded-full bg-primary -mt-[6px] ring-4 ring-background z-10"
                />

                {/* Step number */}
                <p
                  className="mt-5 sm:mt-6 font-display text-sm font-semibold tracking-[0.22em] text-primary"
                  data-testid={`child-step-${i + 1}-number`}
                >
                  {String(i + 1).padStart(2, '0')}
                </p>

                {/* Title */}
                <h3
                  className="mt-2 font-display text-lg sm:text-xl font-bold text-foreground max-w-[16rem]"
                  data-testid={`child-step-${i + 1}-title`}
                >
                  {step.title}
                </h3>

                {/* Description */}
                <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-[18rem] leading-relaxed">
                  {step.desc}
                </p>
              </li>
            ))}
          </ol>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 sm:mt-14 flex justify-center">
          <Button asChild data-testid="child-seat-cta">
            <Link to="/request-a-ride?childSeat=1">Request with child seat<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
